import React from 'react';
import { Helmet } from 'react-helmet';
import JsonLdList from '../../../common/seo/JsonLdList';
import { SITE_URL, getBreadcrumbSchema, organizationSchema, websiteSchema, localBusinessSchema } from '../../../../utils/seo';
import { buildFaqSchema, buildHowToSchema, buildItemListSchema, buildWebPageSchema, buildProductSchemaWithOffers } from '../../../../utils/schema';
import { getCatalog } from '../../../../data/db';
import pageAssets from '../../../../data/pageAssets';

const WIRE_SHARE_IMAGE = `${SITE_URL}${pageAssets.wireHero}`;

const WiresSeo = ({ seo, faqItems, structuredProducts, productFamilies, buyingGuide }) => {
    const catalog = getCatalog('wires');

    const productSchemas = structuredProducts.map((product) => {
        const variants = catalog
            .filter((series) => product.catalogSeries.includes(series.series))
            .flatMap((series) => series.variants || []);
        return buildProductSchemaWithOffers({ ...product, imageUrl: `${SITE_URL}${pageAssets[product.imageKey]}` }, variants);
    });

    // Wire size guide for AEO — structured data AI engines can extract
    const wireSizeGuideSchema = {
        "@context": "https://schema.org",
        "@type": "HowTo",
        "name": "How to Choose Wire Size for House Wiring in India",
        "description": "Guide to selecting the right wire size (sq mm) for different electrical circuits in Indian homes.",
        "step": [
            {
                "@type": "HowToStep",
                "position": 1,
                "name": "Lighting Circuits",
                "text": "Use 1.0 sq mm or 1.5 sq mm fire resistant wire for lighting circuits. This handles standard LED and CFL fixtures safely."
            },
            {
                "@type": "HowToStep",
                "position": 2,
                "name": "Power Socket Circuits",
                "text": "Use 2.5 sq mm wire for 5A and 15A power sockets that power TVs, computers, fans and small appliances."
            },
            {
                "@type": "HowToStep",
                "position": 3,
                "name": "Heavy Appliance Circuits",
                "text": "Use 4.0 sq mm or 6.0 sq mm wire for air conditioners, geysers, water heaters and induction cooktops."
            },
            {
                "@type": "HowToStep",
                "position": 4,
                "name": "Main Supply Line",
                "text": "Use 6.0 sq mm or 10.0 sq mm wire for the main electrical supply line from the meter to the distribution board."
            }
        ]
    };

    const schemas = [
        organizationSchema,
        websiteSchema,
        localBusinessSchema,
        getBreadcrumbSchema([
            { name: 'Home', url: `${SITE_URL}/` },
            { name: 'Wires & Cables', url: `${SITE_URL}/wires` }
        ]),
        buildWebPageSchema({
            name: seo.webPageName,
            url: `${SITE_URL}/wires`,
            description: seo.webPageDescription,
            about: [
                { "@type": "Thing", "name": "Electrical cable", "sameAs": "https://www.wikidata.org/wiki/Q188447" },
                { "@type": "Thing", "name": "Fire Resistant Cables" },
                { "@type": "Thing", "name": "House wiring", "sameAs": "https://www.wikidata.org/wiki/Q330582" },
                { "@type": "Thing", "name": "Copper conductor", "sameAs": "https://www.wikidata.org/wiki/Q753" }
            ],
            speakable: ['.wire-catalog-intro', '.faq-section', '.about-wires-hero-content']
        }),
        ...productSchemas,
        buildItemListSchema({
            name: 'NISHAD Wire Product Families',
            items: productFamilies,
            getName: (name) => name,
            getUrl: () => `${SITE_URL}/wires`
        }),
        buildFaqSchema(faqItems),
        wireSizeGuideSchema,
        ...(buyingGuide ? [buildHowToSchema({
            name: buyingGuide.title,
            description: buyingGuide.description,
            steps: buyingGuide.items
        })] : [])
    ];

    return (
        <>
            <Helmet>
                <title>{seo.title}</title>
                <meta name="description" content={seo.description} />
                <meta name="keywords" content={seo.keywords} />
                <meta name="subject" content={seo.subject} />
                <meta name="abstract" content={seo.abstract} />
                <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
                <meta name="author" content="Nishad Cables" />
                <link rel="canonical" href={`${SITE_URL}/wires`} />
                <link rel="alternate" hrefLang="en-IN" href={`${SITE_URL}/wires`} />
                <link rel="alternate" hrefLang="en" href={`${SITE_URL}/wires`} />

                <meta name="geo.region" content="IN-RJ" />
                <meta name="geo.placename" content="Alwar" />
                <meta name="geo.position" content="27.5530; 76.6346" />
                <meta name="ICBM" content="27.5530, 76.6346" />

                <meta property="og:type" content="website" />
                <meta property="og:title" content={seo.title} />
                <meta property="og:description" content={seo.ogDescription} />
                <meta property="og:image" content={WIRE_SHARE_IMAGE} />
                <meta property="og:image:alt" content="NISHAD fire resistant wires and cables — UltraGuard ZHFRLS and Heat Shield range" />
                <meta property="og:image:width" content="1200" />
                <meta property="og:image:height" content="630" />
                <meta property="og:url" content={`${SITE_URL}/wires`} />
                <meta property="og:site_name" content="NISHAD" />
                <meta property="og:locale" content="en_IN" />
                <meta property="article:modified_time" content="2026-05-23T00:00:00+05:30" />

                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={seo.title} />
                <meta name="twitter:description" content={seo.twitterDescription} />
                <meta name="twitter:image" content={WIRE_SHARE_IMAGE} />
            </Helmet>
            <JsonLdList items={schemas} />
        </>
    );
};

export default WiresSeo;

